import { type ReactNode } from 'react'
import { usePermission, useAnyPermission, useCanAccessNav } from '@/lib/permissions'
import { Navigate } from '@tanstack/react-router'

interface PermissionGuardProps {
  /**
   * The permission to check
   */
  permission?: string
  /**
   * Alternative: check if user has ANY of these permissions
   */
  anyPermission?: string[]
  /**
   * Alternative: check navigation access
   */
  navItem?: string
  /**
   * Content to render if user has permission
   */
  children: ReactNode
  /**
   * Content to render if user doesn't have permission (optional)
   */
  fallback?: ReactNode
  /**
   * If true, redirects to access denied page instead of rendering fallback
   */
  redirectOnFail?: boolean
}

/**
 * Component that conditionally renders children based on user permissions.
 * 
 * Usage:
 * ```tsx
 * // Single permission
 * <PermissionGuard permission="can_edit_vouchers">
 *   <button>Edit Voucher</button>
 * </PermissionGuard>
 * 
 * // Any of multiple permissions
 * <PermissionGuard anyPermission={['can_edit_vouchers', 'can_delete_vouchers']}>
 *   <ActionMenu />
 * </PermissionGuard>
 * 
 * // Navigation permission
 * <PermissionGuard navItem="reporting">
 *   <ReportingContent />
 * </PermissionGuard>
 * 
 * // With fallback
 * <PermissionGuard permission="can_export_data" fallback={<p>No export access</p>}>
 *   <ExportButton />
 * </PermissionGuard>
 * ```
 */
export function PermissionGuard({
  permission,
  anyPermission,
  navItem,
  children,
  fallback = null,
  redirectOnFail = false,
}: PermissionGuardProps) {
  // Check single permission
  const hasSinglePerm = usePermission(permission || '')
  
  // Check any of multiple permissions
  const hasAnyPerm = useAnyPermission(anyPermission || [])
  
  // Check nav access
  const canAccessNavItem = useCanAccessNav(navItem || '')
  
  // Determine if user has access
  let hasAccess = false
  
  if (permission) {
    hasAccess = hasSinglePerm
  } else if (anyPermission && anyPermission.length > 0) {
    hasAccess = hasAnyPerm
  } else if (navItem) {
    hasAccess = canAccessNavItem
  } else {
    // No permission specified, allow access
    hasAccess = true
  }
  
  if (hasAccess) {
    return <>{children}</>
  }
  
  if (redirectOnFail) {
    // @ts-expect-error - route may not be registered but redirect works
    return <Navigate to="/auth/access-denied" />
  }
  
  return <>{fallback}</>
}

/**
 * Hook version of PermissionGuard for programmatic checks
 */
export function useHasAccess(options: {
  permission?: string
  anyPermission?: string[]
  navItem?: string
}): boolean {
  const { permission, anyPermission, navItem } = options
  
  const hasSinglePerm = usePermission(permission || '')
  const hasAnyPerm = useAnyPermission(anyPermission || [])
  const canAccessNavItem = useCanAccessNav(navItem || '')
  
  if (permission) {
    return hasSinglePerm
  } else if (anyPermission && anyPermission.length > 0) {
    return hasAnyPerm
  } else if (navItem) {
    return canAccessNavItem
  }
  
  return true
}

/**
 * Higher-order component for route-level permission protection
 */
export function withPermission<P extends object>(
  Component: React.ComponentType<P>,
  permission: string,
  options?: { redirectOnFail?: boolean; fallback?: ReactNode }
) {
  return function PermissionProtectedComponent(props: P) {
    return (
      <PermissionGuard
        permission={permission}
        redirectOnFail={options?.redirectOnFail}
        fallback={options?.fallback}
      >
        <Component {...props} />
      </PermissionGuard>
    )
  }
}

/**
 * Component for showing access denied message
 */
export function AccessDenied({ message }: { message?: string }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-8rem)] px-4">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/20 mb-4">
          <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
          Access Denied
        </h2>
        <p className="text-neutral-600 dark:text-neutral-400 max-w-md">
          {message || "You don't have permission to access this resource. Please contact your administrator if you believe this is an error."}
        </p>
      </div>
    </div>
  )
}
